import { FC, useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'

import QueueNumbersAPI from '@api/queueNumbersAPI'

import { ID } from '@models/base'
import { QueueNodeModel } from '@models/queueNode'

import { usePermissions } from '@modules/permissions/core/PermissionsProvider'

import { PermissionEnum } from '@/constants/permission'
import { useToast } from '@/hooks'
import ArrayUtil from '@/utils/arrayUtil'
import AxiosUtil from '@/utils/axiosUtil'
import TypeUtil from '@/utils/typeUtil'

import { QueueEmptyMessage } from '../QueueEmptyMessage'
import { QueueNodeBubble } from '../QueueNodeBubble'
import { QueueNodeDragHandle } from '../QueueNodeDragHandle'

type Props = {
  queueId: ID
  queueNodes: Array<QueueNodeModel>
  isCompletable?: boolean
  isCallable?: boolean
  isRedeployable?: boolean
  isDraggable?: boolean
  onEdit: (node: QueueNodeModel) => void
  onComplete?: (node: QueueNodeModel) => void
}

const QueueProcessNodeListContainer: FC<Props> = ({
  queueNodes,
  queueId,
  isCompletable,
  isRedeployable,
  isCallable,
  isDraggable,
  onEdit,
  onComplete,
}) => {
  const toast = useToast()
  const [nodes, setNodes] = useState<Array<QueueNodeModel>>([])
  const { hasPermissions } = usePermissions()
  const isMoveable = hasPermissions(PermissionEnum.QUEUE_NUMBER_MOVE) && isDraggable

  const updateQueuePosition = async (queueNumberId: ID, destination: number) => {
    try {
      await QueueNumbersAPI.moveQueueNode({
        source_queue_id: queueId,
        queue_number_id: queueNumberId,
        destination_position: destination + 1,
      })
    } catch (err) {
      if (AxiosUtil.isAxiosError(err) && err.response?.data.message) {
        toast.error(err.response.data.message)
      }
      console.error(err)
    }
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const items = ArrayUtil.reorder<QueueNodeModel>(nodes, result.source.index, result.destination.index)

    setNodes(items)

    updateQueuePosition(items[result.destination.index].queue_number_id, result.destination.index)
  }

  useEffect(() => {
    if (queueNodes) {
      setNodes(queueNodes)
    }
  }, [queueNodes])

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="queue-process">
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {TypeUtil.isEmpty(nodes) && <QueueEmptyMessage />}

            {nodes.map((node, index) => (
              <Draggable
                key={node.queue_number_id}
                isDragDisabled={!isMoveable}
                draggableId={node.queue_number_id!}
                index={index}
              >
                {(draggableProvided) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    style={draggableProvided.draggableProps.style}
                  >
                    <QueueNodeBubble
                      node={node}
                      queueId={queueId}
                      isCallable={isCallable}
                      isCompletable={isCompletable}
                      isRedeployable={isRedeployable}
                      draggableHandler={
                        isMoveable ? <QueueNodeDragHandle {...draggableProvided.dragHandleProps} /> : undefined
                      }
                      onEdit={onEdit}
                      onComplete={onComplete}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export { QueueProcessNodeListContainer }
